import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Upload, FolderOpen, Play, Pause, Download, X, Loader2, Sparkles,
  Layers3, Music2, Mic2, SlidersHorizontal, Send, CheckCircle2, AlertCircle,
  Lock, Zap, ChevronRight,
} from "lucide-react";
import { api, APP_ORIGIN } from "@/lib/api";
import { ContextualHelp } from "@/components/help/ContextualHelp";

/**
 * Stem Studio — dedicated full-page UI for LALAL.AI stem separation.
 *
 * Flow:
 *   1. Drop / browse an audio file (or pick from the imaging library).
 *   2. Pick one of 4 presets (Karaoke · Standard · Producer · Everything).
 *   3. Job appears in the Queue with a live progress bar while LALAL.AI
 *      splits (poll interval = 3 s).
 *   4. On success, the Result card renders with one waveform-styled row
 *      per stem, per-stem play + download buttons, and 3 "Send to" chips.
 *
 * Auth-gated by the outer <DashboardLayout>. LALAL.AI BYOK gate is
 * enforced by the backend endpoint (402 byok_required → toast + link).
 */

// ── Presets ─────────────────────────────────────────────────────────
const PRESETS = [
  {
    id: "karaoke",
    name: "Karaoke",
    description: "Vocals + full instrumental. Perfect for sing-alongs.",
    swatches: ["#ff6b9d", "#4a9eff"],
    stem_count: "2 stems",
  },
  {
    id: "standard",
    name: "Standard",
    description: "Vocals · drums · bass · other. The everyday split.",
    swatches: ["#ff6b9d", "#ffb84a", "#7c5cff", "#4a9eff"],
    stem_count: "4 stems",
  },
  {
    id: "producer",
    name: "Producer",
    description: "Vocals · drums · bass · piano · both guitars · other.",
    swatches: ["#ff6b9d", "#ffb84a", "#7c5cff", "#f472b6", "#fb923c", "#facc15", "#4a9eff"],
    stem_count: "7 stems",
  },
  {
    id: "everything",
    name: "Everything",
    description: "All 10 stems including synth · strings · wind.",
    swatches: ["#ff6b9d", "#ffb84a", "#7c5cff", "#f472b6", "#fb923c", "#facc15",
               "#a78bfa", "#22d3ee", "#84cc16", "#4a9eff"],
    stem_count: "10 stems",
    badge: "Advanced",
  },
];

const STEM_COLOR = {
  vocals:          "#ff6b9d",
  drums:           "#ffb84a",
  bass:            "#7c5cff",
  piano:           "#f472b6",
  electric_guitar: "#fb923c",
  acoustic_guitar: "#facc15",
  synthesizer:     "#a78bfa",
  strings:         "#22d3ee",
  wind:            "#84cc16",
  other:           "#4a9eff",
  instrumental:    "#4a9eff",
};

// ── Deterministic waveform bars (mocked visual — no decoding) ────────
function useWaveformBars(seed = 0, count = 46) {
  return useMemo(() => {
    let s = seed || 42;
    const bars = [];
    for (let i = 0; i < count; i++) {
      s = (s * 9301 + 49297) % 233280;
      const rand = s / 233280;
      // Sine + noise → nice organic look
      const h = 0.25 + 0.6 * Math.abs(Math.sin(i * 0.45) + (rand - 0.5) * 0.6);
      bars.push(Math.max(0.12, Math.min(1.0, h)));
    }
    return bars;
  }, [seed, count]);
}


function WaveformStrip({ seedFrom = "wave", color = "#5f6779", height = 32 }) {
  const seed = useMemo(() => {
    let acc = 0;
    for (let i = 0; i < seedFrom.length; i++) acc += seedFrom.charCodeAt(i) * (i + 1);
    return acc;
  }, [seedFrom]);
  const bars = useWaveformBars(seed);
  return (
    <div
      className="flex items-center gap-[2px] rounded-md px-2.5 overflow-hidden flex-1"
      style={{ height, background: "#161923" }}
    >
      {bars.map((h, i) => (
        <div
          key={i}
          style={{
            width: "2.5px",
            height: `${Math.round(h * (height - 4))}px`,
            background: color,
            opacity: 0.7 + 0.3 * (i % 3 === 0 ? 1 : 0),
            borderRadius: 2,
            flexShrink: 0,
          }}
        />
      ))}
    </div>
  );
}


// ── The page ─────────────────────────────────────────────────────────
export default function StemStudio() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [selectedPreset, setSelectedPreset] = useState("standard");
  const [dragOver, setDragOver] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libraryAssets, setLibraryAssets] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [playingId, setPlayingId] = useState(null);
  const [access, setAccess] = useState(null);   // {allowed, reason, current_plan, trial_expires_at, lalal_connected}
  const audioRef = useRef(null);
  const pollRef = useRef(null);

  // ── Access check on mount ─────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/production/stem-studio/access");
        setAccess(r.data || { allowed: false, reason: "unknown" });
      } catch {
        setAccess({ allowed: false, reason: "network_error" });
      }
    })();
  }, []);

  // ── Load current jobs on mount ─────────────────────────────────
  const loadJobs = useCallback(async () => {
    try {
      const r = await api.get("/production/stem-jobs?limit=30");
      setJobs(r.data?.jobs || []);
    } catch (e) {
      /* ignore */
    } finally {
      setLoadingJobs(false);
    }
  }, []);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  // ── Poll active jobs every 3s ──────────────────────────────────
  const hasActive = jobs.some((j) => !["succeeded", "failed", "canceled"].includes((j.status || "").toLowerCase()));
  useEffect(() => {
    if (!hasActive) return;
    pollRef.current = setTimeout(loadJobs, 3000);
    return () => { if (pollRef.current) clearTimeout(pollRef.current); };
  }, [hasActive, jobs.length, loadJobs]);

  // ── Upload flow ────────────────────────────────────────────────
  //
  // Accepts EITHER a single File OR a FileList/array for batch mode.
  // Each file is uploaded + split sequentially so we don't hammer
  // LALAL.AI with 10 concurrent uploads on a big batch drop.
  const startSeparationFromFile = async (fileOrFiles) => {
    const files = fileOrFiles instanceof FileList
      ? Array.from(fileOrFiles)
      : Array.isArray(fileOrFiles) ? fileOrFiles : (fileOrFiles ? [fileOrFiles] : []);
    if (files.length === 0) return;

    // Reject non-audio + very-large files up front
    const valid = files.filter((f) => {
      if (!f.type.startsWith("audio/") && !/\.(mp3|wav|flac|m4a|ogg|aac)$/i.test(f.name)) {
        toast.error(`Skipping "${f.name}" — not an audio file`);
        return false;
      }
      return true;
    });
    if (valid.length === 0) return;

    if (valid.length > 1) {
      toast.message(`Batch mode: ${valid.length} tracks queued with the ${PRESETS.find((p) => p.id === selectedPreset).name} preset…`, { duration: 4500 });
    }

    for (let i = 0; i < valid.length; i++) {
      const file = valid[i];
      const label = valid.length > 1 ? `(${i + 1}/${valid.length}) ${file.name}` : file.name;
      try {
        toast.message(`Uploading ${label}…`, { duration: 2500 });
        const fd = new FormData();
        fd.append("file", file);
        fd.append("kind", "bed");
        const up = await api.post("/imaging/assets/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const assetId = up?.data?.asset_id;
        if (!assetId) throw new Error("Upload returned no asset_id");
        await triggerSplit(assetId, file.name);
      } catch (e) {
        const d = e?.response?.data?.detail;
        // Don't spam toasts for every batch failure — just log to console
        if (valid.length === 1) {
          toast.error(typeof d === "string" ? d : (d?.message || "Upload failed"));
        } else {
          console.warn(`Batch upload failed for ${file.name}:`, d || e.message);
        }
      }
    }
  };

  const startSeparationFromAsset = async (asset) => {
    setLibraryOpen(false);
    await triggerSplit(asset.asset_id, asset.label || asset.name || "asset.wav");
  };

  const triggerSplit = async (assetId, fileName) => {
    toast.message(`Splitting "${fileName}" with ${PRESETS.find((p) => p.id === selectedPreset).name} preset…`);
    try {
      const r = await api.post("/production/stem-separate", {
        asset_id: assetId,
        file_name: fileName,
        preset:    selectedPreset,
      });
      const d = r.data || {};
      if (d.stub) {
        toast.error(d.message || "Connect LALAL.AI to use stem separation");
        return;
      }
      // Success returns quickly (LALAL.AI takes ~20-60s but our endpoint waits synchronously)
      if (d.status === "succeeded") {
        toast.success(`Done — ${(d.stems || []).length} stems ready`);
      }
      loadJobs();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.error === "byok_required") {
        toast.error("Connect LALAL.AI (primary) or Replicate in AI Provider Center", {
          duration: 6000,
          action: { label: "Connect", onClick: () => navigate("/dashboard/providers") },
        });
      } else {
        toast.error(typeof d === "string" ? d : (d?.message || "Stem separation failed"));
      }
    }
  };

  // ── Library picker ────────────────────────────────────────────
  const openLibrary = async () => {
    setLibraryOpen(true);
    setLibraryLoading(true);
    try {
      const r = await api.get("/imaging/library?kind=bed&limit=60");
      setLibraryAssets(r.data?.assets || r.data?.items || []);
    } catch {
      setLibraryAssets([]);
    } finally {
      setLibraryLoading(false);
    }
  };

  // ── Drag / drop (multi-file batch mode) ──────────────────────
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) startSeparationFromFile(files);
  };

  // ── Playback ──────────────────────────────────────────────────
  const togglePlay = (stemAssetId, audioUrl) => {
    if (playingId === stemAssetId) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    if (audioRef.current) audioRef.current.pause();
    const a = new Audio(`${APP_ORIGIN}${audioUrl}`);
    audioRef.current = a;
    a.play().catch(() => toast.error("Playback failed"));
    a.onended = () => setPlayingId(null);
    setPlayingId(stemAssetId);
  };

  // ── Send to actions ───────────────────────────────────────────
  const sendToImaging = () => {
    toast.success("Stems are already in your Imaging Library — opening…");
    navigate("/dashboard/imaging");
  };
  const sendToDaw = async (job) => {
    try {
      const r = await api.post(`/production/stem-jobs/${job.job_id}/send-to-daw`, {});
      const link = r?.data?.deep_link || `/dashboard/production-suite?stems_job=${job.job_id}`;
      toast.success(`Sending ${r?.data?.stem_count || job.stems.length} stems to the DAW…`);
      navigate(link);
    } catch {
      navigate(`/dashboard/production-suite?stems_job=${job.job_id}`);
    }
  };
  const sendToKaraoke = (job) => {
    // Update #167 — route to the dedicated Karaoke Studio landing page
    // (auto-opens the render modal on the picked asset). This replaces
    // the older deep-link into Imaging Studio → Beds & FX tab.
    if (job.asset_id) {
      toast.success("Opening Karaoke Studio with this track…");
      navigate(`/dashboard/karaoke-studio?asset=${job.asset_id}`);
    } else {
      toast.message("Karaoke needs the source track — open it from Imaging Studio Beds & FX");
    }
  };

  // ── Split active / completed / failed ─────────────────────────
  //
  // Jobs are grouped into 3 buckets so nothing "disappears" from the UI.
  //   • activeJobs  — anything still running (starting / queued / processing / …)
  //   • completedJobs — successful runs, get the full ResultCard treatment
  //   • failedJobs  — errored/canceled runs, shown in a compact row so the
  //                   user can retry rather than wondering where their file went.
  const activeJobs = jobs.filter((j) => !["succeeded", "failed", "canceled"].includes((j.status || "").toLowerCase()));
  const completedJobs = jobs.filter((j) => (j.status || "").toLowerCase() === "succeeded");
  const failedJobs = jobs.filter((j) => ["failed", "canceled"].includes((j.status || "").toLowerCase()));

  return (
    <div className="min-h-screen text-white" style={{ background: "#0f1117" }}>
      <div className="max-w-[1180px] mx-auto px-8 py-8 pb-16">
        {/* ── Locked / Upgrade screen ─────────────────────────── */}
        {access && !access.allowed && (
          <LockedScreen access={access} onUpgrade={() => navigate("/dashboard/billing?tab=plan")} />
        )}

        {(!access || access.allowed) && (
        <>
        {/* ── Header ────────────────────────────────────────── */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="type-page-title text-white flex items-center gap-2">
              <Layers3 size={22} className="text-[#4a9eff]" />
              Stem Studio
            </h1>
            <div className="type-list-subtitle text-[#9aa3b5] mt-1.5 max-w-[520px] leading-snug">
              Split any music track into clean, isolated stems using LALAL.AI. Karaoke, drums, bass, piano, guitars, synth, strings, wind, and vocals — all in one place.
            </div>
          </div>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#4a9eff] text-[#06121f] type-button-label hover:opacity-90 transition"
            data-testid="stem-studio-new-separation"
          >
            <Sparkles size={13} /> New separation
          </button>
        </div>

        <div className="flex justify-end mb-3">
          <ContextualHelp featureKey="stem-studio" />
        </div>

        {/* ── Trial banner ──────────────────────────────────── */}
        {access?.reason === "trial_active" && access.trial_expires_at && (
          <TrialBanner
            expiresAt={access.trial_expires_at}
            onUpgrade={() => navigate("/dashboard/billing?tab=plan")}
          />
        )}

        {/* ── Upload zone ───────────────────────────────────── */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className="relative overflow-hidden rounded-[14px] mb-8 p-10 text-center transition"
          style={{
            border: `1.5px dashed ${dragOver ? "#4a9eff" : "#343b4f"}`,
            background: dragOver ? "rgba(74,158,255,0.05)" : "#161923",
          }}
          data-testid="stem-studio-upload-zone"
        >
          {/* Radial glow */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "radial-gradient(circle at 50% 0%, rgba(74,158,255,0.10), transparent 60%)",
            }}
          />
          <div className="relative">
            <div className="w-11 h-11 mx-auto mb-4 rounded-[12px] flex items-center justify-center"
                 style={{ background: "rgba(74,158,255,0.12)" }}>
              <Upload size={20} className="text-[#4a9eff]" />
            </div>
            <div className="type-section-heading text-white mb-1.5">
              {dragOver ? "Drop it now" : "Drop one file — or many for batch mode"}
            </div>
            <div className="type-list-subtitle text-[#9aa3b5] mb-5">
              MP3 · WAV · FLAC · M4A up to 250 MB each. Longer tracks may use more LALAL.AI minutes.
            </div>
            <div className="flex items-center justify-center gap-2.5">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-[#343b4f] bg-transparent text-white type-button-label hover:border-[#4a9eff] transition"
                data-testid="stem-studio-upload-btn"
              >
                <Upload size={13} /> Upload file
              </button>
              <button
                type="button"
                onClick={openLibrary}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-[#343b4f] bg-transparent text-white type-button-label hover:border-[#4a9eff] transition"
                data-testid="stem-studio-library-btn"
              >
                <FolderOpen size={13} /> Browse Asset Library
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*,.mp3,.wav,.flac,.m4a,.ogg,.aac"
              multiple
              className="hidden"
              onChange={(e) => {
                const files = e.target.files;
                if (files && files.length > 0) startSeparationFromFile(files);
                if (e.target) e.target.value = "";
              }}
              data-testid="stem-studio-file-input"
            />
          </div>
        </div>

        {/* ── Presets ───────────────────────────────────────── */}
        <div className="type-list-title text-[#5f6779] uppercase tracking-wider mb-3.5" style={{ fontSize: 12, letterSpacing: "0.06em" }}>
          Choose what you need
        </div>
        <div className="grid grid-cols-4 gap-3 mb-9">
          {PRESETS.map((p) => {
            const active = selectedPreset === p.id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setSelectedPreset(p.id)}
                className="text-left p-4 rounded-[10px] transition"
                style={{
                  background: active
                    ? "linear-gradient(180deg, rgba(74,158,255,0.12), #1a1e2a 60%)"
                    : "#1a1e2a",
                  border: `1px solid ${active ? "#4a9eff" : "#262b3a"}`,
                }}
                data-testid={`stem-studio-preset-${p.id}`}
              >
                <div className="flex items-center gap-1 mb-3">
                  {p.swatches.map((c, i) => (
                    <div key={i} style={{
                      width: 18, height: 6, borderRadius: 3, background: c,
                    }} />
                  ))}
                </div>
                <div className="flex items-center justify-between mb-1">
                  <div className="type-list-title text-white" style={{ fontSize: 13.5, fontWeight: 600 }}>
                    {p.name}
                  </div>
                  {p.badge && (
                    <div className="type-status-badge text-[#FF8A1F] bg-[#FF8A1F]/10 px-1.5 py-0.5 rounded" style={{ fontSize: 9.5 }}>
                      {p.badge}
                    </div>
                  )}
                </div>
                <div className="type-list-subtitle text-[#9aa3b5] leading-snug" style={{ fontSize: 11.5 }}>
                  {p.description}
                </div>
                <div className="type-status-badge text-[#5f6779] mt-1.5" style={{ fontSize: 10.5 }}>
                  {p.stem_count}
                </div>
              </button>
            );
          })}
        </div>

        {/* ── Queue ─────────────────────────────────────────── */}
        {activeJobs.length > 0 && (
          <>
            <div className="type-list-title text-[#5f6779] uppercase tracking-wider mb-3.5" style={{ fontSize: 12 }}>
              Processing queue
            </div>
            <div className="flex flex-col gap-2.5 mb-9" data-testid="stem-studio-queue">
              {activeJobs.map((j) => (
                <QueueRow key={j.job_id} job={j} onReload={loadJobs} />
              ))}
            </div>
          </>
        )}

        {/* ── Failed ─────────────────────────────────────────── */}
        {failedJobs.length > 0 && (
          <>
            <div className="type-list-title text-[#5f6779] uppercase tracking-wider mb-3.5" style={{ fontSize: 12 }}>
              Needs attention ({failedJobs.length})
            </div>
            <div className="flex flex-col gap-2.5 mb-9" data-testid="stem-studio-failed-list">
              {failedJobs.map((j) => (
                <FailedRow key={j.job_id} job={j} onRetry={() => triggerSplit(j.asset_id, j.file_name)} />
              ))}
            </div>
          </>
        )}

        {/* ── Ready ─────────────────────────────────────────── */}
        {loadingJobs ? (
          <div className="type-list-subtitle text-[#5f6779] text-center py-8">
            <Loader2 size={18} className="animate-spin inline mr-2" /> Loading your stem library…
          </div>
        ) : completedJobs.length === 0 ? (
          <div
            className="rounded-[10px] py-10 px-6 text-center"
            style={{ background: "#161923", border: "1px solid #262b3a" }}
          >
            <div className="type-list-subtitle text-[#5f6779]">
              No stems yet — drop a file above and pick a preset to get started.
            </div>
          </div>
        ) : (
          <>
            <div className="type-list-title text-[#5f6779] uppercase tracking-wider mb-3.5" style={{ fontSize: 12 }}>
              Ready to use ({completedJobs.length})
            </div>
            {completedJobs.map((j) => (
              <ResultCard
                key={j.job_id}
                job={j}
                playingId={playingId}
                togglePlay={togglePlay}
                sendToImaging={sendToImaging}
                sendToDaw={sendToDaw}
                sendToKaraoke={sendToKaraoke}
              />
            ))}
          </>
        )}

        {/* ── Tier note ─────────────────────────────────────── */}
        <div
          className="flex items-center gap-2.5 mt-3 rounded-[10px] px-4 py-3.5"
          style={{ background: "#161923", border: "1px solid #262b3a" }}
        >
          <span
            className="type-status-badge px-2 py-0.5 rounded"
            style={{ background: "rgba(74,158,255,0.12)", color: "#4a9eff", fontSize: 10.5 }}
          >
            LALAL.AI
          </span>
          <div className="type-list-subtitle text-[#9aa3b5]" style={{ fontSize: 12.5 }}>
            Every split uses LALAL.AI minutes from your connected subscription.
            Replicate/Demucs is the automatic fallback if LALAL.AI is unavailable.
          </div>
        </div>
        </>
        )}
      </div>

      {/* ── Library modal ───────────────────────────────────── */}
      {libraryOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
          onClick={() => setLibraryOpen(false)}
          data-testid="stem-studio-library-modal"
        >
          <div
            className="w-full max-w-2xl bg-[#0f1117] border border-[#262b3a] rounded-lg shadow-2xl p-6 max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="type-section-heading text-white">Pick a track from your library</div>
              <button onClick={() => setLibraryOpen(false)} className="text-[#9aa3b5] hover:text-white">
                <X size={16} />
              </button>
            </div>
            {libraryLoading ? (
              <div className="text-center py-6 text-[#5f6779]">
                <Loader2 size={16} className="animate-spin inline mr-2" /> Loading library…
              </div>
            ) : libraryAssets.length === 0 ? (
              <div className="text-center py-6 text-[#5f6779] type-list-subtitle">
                No music beds in your library yet. Upload one above or head to Imaging Studio → Beds & FX first.
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {libraryAssets.map((a) => (
                  <button
                    key={a.asset_id}
                    type="button"
                    onClick={() => startSeparationFromAsset(a)}
                    className="text-left p-3 rounded-md border border-[#262b3a] bg-[#161923] hover:border-[#4a9eff] transition"
                    data-testid={`stem-studio-library-item-${a.asset_id}`}
                  >
                    <div className="type-list-title text-white truncate">{a.label || a.name || "Untitled"}</div>
                    <div className="type-list-subtitle text-[#5f6779] mt-1" style={{ fontSize: 11 }}>
                      {a.genre || "Music bed"} · {(a.duration_s ? Math.round(a.duration_s) + "s · " : "")}{Math.round((a.size_bytes || 0) / 1024)} KB
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


// ── QueueRow ─────────────────────────────────────────────────────────
function QueueRow({ job }) {
  const status = (job.status || "").toLowerCase();
  const label = {
    starting:   "Starting",
    queued:     "Queued",
    separating: "Splitting…",
    processing: "Processing",
    rendering:  "Rendering",
    succeeded:  "Done",
    failed:     "Failed",
  }[status] || status;
  const badgeColor =
    status === "succeeded" ? "#3ddc97" :
    status === "failed"    ? "#ff6b6b" :
    status === "queued"    ? "#5f6779" : "#4a9eff";

  return (
    <div
      className="flex items-center gap-4 px-4.5 py-4 rounded-[10px]"
      style={{ background: "#1a1e2a", border: "1px solid #262b3a" }}
    >
      <div
        className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0"
        style={{ background: "#161923", color: "#9aa3b5" }}
      >
        <Music2 size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="type-list-title text-white truncate" title={job.file_name}>
          {job.file_name}
        </div>
        <div className="type-status-badge text-[#5f6779] mt-0.5" style={{ fontSize: 11.5 }}>
          {job.preset_label} preset · started {timeAgo(job.created_at)}
        </div>
      </div>
      <div
        className="rounded overflow-hidden flex-shrink-0"
        style={{ width: 90, height: 4, background: "#262b3a" }}
      >
        <div style={{ width: `${job.progress}%`, height: "100%", background: badgeColor, transition: "width 0.4s" }} />
      </div>
      <div
        className="type-status-badge font-semibold px-2.5 py-1 rounded whitespace-nowrap flex-shrink-0"
        style={{ color: badgeColor, background: `${badgeColor}22`, fontSize: 11.5 }}
      >
        {label}
      </div>
    </div>
  );
}


// ── FailedRow — shows failed jobs so files never appear to "vanish" ──
function FailedRow({ job, onRetry }) {
  const err = job.error || "Something went wrong. Try again — LALAL.AI usually finishes on retry.";
  return (
    <div
      className="flex items-start gap-4 px-4.5 py-4 rounded-[10px]"
      style={{ background: "#1a1e2a", border: "1px solid rgba(255,107,107,0.35)" }}
      data-testid={`stem-studio-failed-${job.job_id}`}
    >
      <div
        className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5"
        style={{ background: "rgba(255,107,107,0.12)", color: "#ff6b6b" }}
      >
        <AlertCircle size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="type-list-title text-white truncate" title={job.file_name}>
          {job.file_name}
        </div>
        <div className="type-status-badge text-[#9aa3b5] mt-0.5 leading-snug" style={{ fontSize: 11.5 }}>
          {err.length > 180 ? err.slice(0, 180) + "…" : err}
        </div>
      </div>
      {job.asset_id && (
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[#9aa3b5] type-button-label border border-[#262b3a] hover:border-[#4a9eff] hover:text-[#4a9eff] transition flex-shrink-0"
          data-testid={`stem-studio-retry-${job.job_id}`}
        >
          <Sparkles size={11} /> Retry
        </button>
      )}
    </div>
  );
}


// ── ResultCard ───────────────────────────────────────────────────────
function ResultCard({ job, playingId, togglePlay, sendToImaging, sendToDaw, sendToKaraoke }) {
  const stems = job.stems || [];
  const [open, setOpen] = React.useState(false);
  return (
    <div
      className="rounded-[14px] p-6 mb-4"
      style={{ background: "#1a1e2a", border: "1px solid #262b3a" }}
      data-testid={`stem-studio-result-${job.job_id}`}
    >
      <div
        className={`flex justify-between items-center cursor-pointer select-none ${open ? "mb-5" : ""}`}
        onClick={() => setOpen((v) => !v)}
        data-testid={`stem-studio-result-toggle-${job.job_id}`}
        role="button"
        aria-expanded={open}
      >
        <div className="flex items-center gap-3 min-w-0">
          <ChevronRight
            size={16}
            className="text-[#5f6779] flex-shrink-0 transition-transform duration-200"
            style={{ transform: open ? "rotate(90deg)" : "none" }}
          />
          <div className="min-w-0">
            <div className="type-list-title text-white truncate" style={{ fontSize: 14.5, fontWeight: 600 }}>
              {job.file_name}
            </div>
            <div className="type-status-badge text-[#5f6779] mt-0.5" style={{ fontSize: 12 }}>
              {job.preset_label} · {stems.length} stems · finished {timeAgo(job.completed_at)}
            </div>
          </div>
        </div>
        {stems.length > 0 && (
          <a
            href={`${APP_ORIGIN}/api/production/stem-jobs/${job.job_id}/download-zip`}
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-white type-button-label border border-[#4a9eff] bg-[#4a9eff]/10 hover:bg-[#4a9eff]/20 transition flex-shrink-0"
            data-testid={`stem-studio-download-zip-${job.job_id}`}
          >
            <Download size={12} /> Download all ({stems.length}) as ZIP
          </a>
        )}
      </div>

      {open && stems.map((s, i) => {
        const cName = STEM_COLOR[s.name] || "#5f6779";
        const isPlaying = playingId === s.asset_id;
        return (
          <div
            key={s.asset_id}
            className="flex items-center gap-3.5 py-3"
            style={{ borderTop: i === 0 ? "none" : "1px solid #262b3a" }}
            data-testid={`stem-studio-stem-${s.name}`}
          >
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: cName, flexShrink: 0 }} />
            <div className="type-list-title text-white flex-shrink-0" style={{ width: 110, fontSize: 13, fontWeight: 600 }}>
              {formatStemName(s.name)}
            </div>
            <WaveformStrip seedFrom={`${job.job_id}-${s.name}`} color={cName} />
            <button
              type="button"
              onClick={() => togglePlay(s.asset_id, s.audio_url)}
              className="w-[30px] h-[30px] rounded-md border border-[#262b3a] text-[#9aa3b5] hover:border-[#4a9eff] hover:text-white flex items-center justify-center transition"
              data-testid={`stem-studio-play-${s.name}`}
              aria-label={isPlaying ? "Pause" : "Play"}
            >
              {isPlaying ? <Pause size={12} /> : <Play size={12} />}
            </button>
            <a
              href={`${APP_ORIGIN}${s.audio_url}?download=1`}
              download={`${formatStemName(s.name).replace(/\s+/g, "_")}.wav`}
              className="w-[30px] h-[30px] rounded-md border border-[#262b3a] text-[#9aa3b5] hover:border-[#4a9eff] hover:text-white flex items-center justify-center transition"
              data-testid={`stem-studio-download-${s.name}`}
              aria-label="Download stem"
              title="Download this stem"
            >
              <Download size={12} />
            </a>
          </div>
        );
      })}

      {/* Send-to / Download chips */}
      {open && (
      <div
        className="flex flex-wrap items-center gap-2.5 mt-4 pt-4"
        style={{ borderTop: "1px solid #262b3a" }}
      >
        <div className="type-list-subtitle text-[#5f6779] mr-1" style={{ fontSize: 12 }}>Send to</div>
        <button
          type="button"
          onClick={sendToImaging}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[#9aa3b5] type-button-label border border-[#262b3a] bg-[#161923] hover:border-[#4a9eff] hover:text-[#4a9eff] transition"
          data-testid={`stem-studio-send-imaging-${job.job_id}`}
        >
          <Send size={11} /> Imaging Studio
        </button>
        <button
          type="button"
          onClick={() => sendToDaw(job)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[#9aa3b5] type-button-label border border-[#262b3a] bg-[#161923] hover:border-[#4a9eff] hover:text-[#4a9eff] transition"
          data-testid={`stem-studio-send-daw-${job.job_id}`}
        >
          <SlidersHorizontal size={11} /> Production Suite
        </button>
        <button
          type="button"
          onClick={() => sendToKaraoke(job)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[#9aa3b5] type-button-label border border-[#262b3a] bg-[#161923] hover:border-[#4a9eff] hover:text-[#4a9eff] transition"
          data-testid={`stem-studio-send-karaoke-${job.job_id}`}
        >
          <Mic2 size={11} /> Karaoke Studio
        </button>

        {/* Separator */}
        <div className="mx-1 w-px self-stretch bg-[#262b3a]" />
        <div className="type-list-subtitle text-[#5f6779] mr-1" style={{ fontSize: 12 }}>Or download</div>
        <a
          href={`${APP_ORIGIN}/api/production/stem-jobs/${job.job_id}/download-zip`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[#9aa3b5] type-button-label border border-[#262b3a] bg-[#161923] hover:border-[#4a9eff] hover:text-[#4a9eff] transition"
          data-testid={`stem-studio-send-download-zip-${job.job_id}`}
          title="Grab all stems as one ZIP — drop straight into your DAW / editor"
        >
          <Download size={11} /> All stems (ZIP)
        </a>
      </div>
      )}
    </div>
  );
}


// ── Utilities ────────────────────────────────────────────────────────
function formatStemName(name) {
  if (!name) return "Stem";
  return name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function timeAgo(iso) {
  if (!iso) return "moments ago";
  try {
    const t = new Date(iso).getTime();
    const dt = Math.max(0, Date.now() - t) / 1000;
    if (dt < 60)     return `${Math.round(dt)}s ago`;
    if (dt < 3600)   return `${Math.round(dt / 60)}m ago`;
    if (dt < 86400)  return `${Math.round(dt / 3600)}h ago`;
    return new Date(iso).toLocaleDateString();
  } catch { return "moments ago"; }
}


// ── LockedScreen — shown when the user's plan doesn't include Stem Studio
function LockedScreen({ access, onUpgrade }) {
  const reason = access.reason;
  const currentPlan = access.current_plan || "free";
  return (
    <div
      className="rounded-[14px] p-10 my-6 text-center"
      style={{
        background: "linear-gradient(180deg, rgba(74,158,255,0.05), #161923 60%)",
        border: "1px solid #262b3a",
      }}
      data-testid="stem-studio-locked"
    >
      <div className="w-14 h-14 mx-auto mb-4 rounded-[14px] flex items-center justify-center"
           style={{ background: "rgba(74,158,255,0.12)" }}>
        <Lock size={22} className="text-[#4a9eff]" />
      </div>
      <div className="type-page-title text-white mb-2">
        {reason === "trial_expired" ? "Your 14-day trial has ended" : "Stem Studio is a Pro Radio feature"}
      </div>
      <div className="type-list-subtitle text-[#9aa3b5] max-w-[520px] mx-auto mb-6 leading-relaxed">
        {reason === "trial_expired" ? (
          <>Your Stem Studio access ran out with your trial. Upgrade to <span className="text-white font-semibold">Pro Radio</span> or <span className="text-white font-semibold">Network</span> to keep splitting tracks — plus everything else BroadcastFlow offers.</>
        ) : (
          <>Stem Studio ships free during your 14-day trial, then it&apos;s included with <span className="text-white font-semibold">Pro Radio</span> and <span className="text-white font-semibold">Network</span> plans. Your current plan (<span className="uppercase">{currentPlan}</span>) doesn&apos;t include it yet.</>
        )}
      </div>

      {/* Feature list */}
      <div className="flex flex-wrap justify-center gap-2 mb-7 max-w-[560px] mx-auto">
        {[
          "All 4 presets — up to 10 stems",
          "LALAL.AI primary + Demucs fallback",
          "Batch mode — drop 10 tracks at once",
          "Send stems to DAW / Karaoke / Imaging",
        ].map((feat) => (
          <div key={feat} className="type-status-badge text-[#9aa3b5] px-3 py-1.5 rounded-full"
               style={{ background: "#1a1e2a", border: "1px solid #262b3a", fontSize: 11.5 }}>
            <CheckCircle2 size={11} className="inline mr-1 text-[#3ddc97]" />
            {feat}
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={onUpgrade}
        className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-[#4a9eff] text-[#06121f] type-button-label hover:opacity-90 transition"
        data-testid="stem-studio-upgrade-btn"
      >
        <Zap size={13} /> See Pro Radio &amp; Network plans
      </button>
      <div className="type-status-badge text-[#5f6779] mt-3" style={{ fontSize: 11 }}>
        Cancel anytime · Instant access after upgrade
      </div>
    </div>
  );
}


// ── TrialBanner — persistent reminder for trial users of days remaining
function TrialBanner({ expiresAt, onUpgrade }) {
  const daysLeft = useMemo(() => {
    try {
      const t = new Date(expiresAt).getTime();
      return Math.max(0, Math.ceil((t - Date.now()) / (24 * 3600 * 1000)));
    } catch { return 0; }
  }, [expiresAt]);

  const urgent = daysLeft <= 3;
  const accent = urgent ? "#ffb84a" : "#4a9eff";

  return (
    <div
      className="rounded-md px-4 py-3 mb-6 flex items-center justify-between gap-3"
      style={{
        background: `${accent}10`,
        border: `1px solid ${accent}40`,
      }}
      data-testid="stem-studio-trial-banner"
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <Zap size={14} style={{ color: accent, flexShrink: 0 }} />
        <div className="type-list-subtitle text-white truncate" style={{ fontSize: 12.5 }}>
          <span className="font-semibold">Trial · {daysLeft} day{daysLeft === 1 ? "" : "s"} left</span>
          <span className="text-[#9aa3b5]"> — Stem Studio is included with Pro Radio and Network after your trial ends.</span>
        </div>
      </div>
      <button
        type="button"
        onClick={onUpgrade}
        className="type-button-label px-3 py-1.5 rounded-md whitespace-nowrap transition hover:opacity-90"
        style={{ background: accent, color: "#06121f", fontSize: 11.5 }}
        data-testid="stem-studio-trial-upgrade"
      >
        Upgrade now
      </button>
    </div>
  );
}
